// MeetingInviteSender.tsx
import { SendCANmail, SendAdHocCANmail } from "api/canmail/CANmailAPI";

export interface AdHocInviteRecipient {
  email: string;
  first_name?: string;
  last_name?: string;
  contact_id?: number;
}

type InviteAttachments = Array<{ name: string; type: string; data: string }> | null;

const MEET_FROM = "meet@streamdata.com";
const INVITE_SUBJECT = "You have been invited to a meeting";

/**
 * The instant-meeting invite HTML. Identical for a saved-list send and an ad-hoc send —
 * only the delivery call differs, so the markup lives here once.
 */
const buildInviteHtml = (meetingName: string, url: string): string => `<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="x-apple-disable-message-reformatting">
    <title>Meeting Invitation</title>
    <style type="text/css">
      /* Base Styles */
      body {
        margin: 0;
        padding: 0;
        -webkit-text-size-adjust: 100%;
        background-color: #e8f3fc;
        font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
        color: #333;
      }
      table {
        border-collapse: collapse;
      }
      a {
        color: #1a82e2;
        text-decoration: none;
      }
      /* Responsive Layout */
      @media only screen and (min-width: 520px) {
        .container {
          width: 500px !important;
        }
      }
      @media only screen and (max-width: 520px) {
        .container {
          width: 100% !important;
          padding: 0 10px;
        }
      }
    </style>
  </head>
  <body>
    <table width="100%" bgcolor="#e8f3fc" cellpadding="0" cellspacing="0">
      <tr>
        <td align="center">
          <!-- Container -->
          <table class="container" cellpadding="0" cellspacing="0" style="background-color: #ffffff; margin: 20px 0; border-radius: 8px; overflow: hidden; box-shadow: 0px 2px 6px rgba(0,0,0,0.15);">
            <!-- Header -->
            <tr>
              <td align="center" style="padding: 20px; background-color: #64b5f6;">
                <h1 style="margin: 0; font-size: 24px; font-weight: 400; color: #ffffff;">You Have Been Invited to a CANmeet Meeting</h1>
              </td>
            </tr>
            <!-- Meeting Details -->
            <tr>
              <td align="center" style="padding: 20px;">
                <h2 style="margin: 0; margin-bottom: 10px; font-size: 20px; font-weight: 400;">Meeting Name: ${meetingName}</h2>
                <div style="margin-top: 10px; font-size: 18px; line-height: 1.4;">
                  <h3 style="margin: 0; font-size: 18px; font-weight: 400; line-height: 140%; text-align: center; color: #ba372a;">
                    The meeting room is live.
                  </h3>


                </div>
              </td>
            </tr>
            <!-- Join Button -->
            <tr>
              <td align="center" style="padding: 20px;">
                <a href="${url}" target="_blank" style="display: inline-block; background-color: #64b5f6; color: #ffffff; padding: 12px 24px; border-radius: 4px; font-size: 16px; font-weight: 600;">
                  Click To Join
                </a>
              </td>
            </tr>
            <!-- Alternative Link -->
            <tr>
              <td align="center" style="padding: 10px 20px 20px; font-size: 14px; color: #666;">
                <p style="margin: 0;">Or copy and paste the following link into your browser:</p>
                <p style="margin: 5px 0 0;">
                  <a href="${url}" target="_blank" style="word-break: break-all;">${url}</a>
                </p>
              </td>
            </tr>
          </table>
          <!-- End Container -->
        </td>
      </tr>
    </table>
  </body>
</html>`;

/** Send an instant-meeting invite to a saved mail list. */
export const sendMeetingInvite = async (
  listId: string,
  meetingName: string,
  url: string,
  attachments: InviteAttachments = []
) => {
  // Convert the listId from string to number
  const id = parseInt(listId, 10);

  return await SendCANmail(
    id, // list_id (number)
    MEET_FROM, // from
    MEET_FROM, // reply_to
    INVITE_SUBJECT, // subject
    buildInviteHtml(meetingName, url), // email_contents
    attachments && attachments.length ? attachments : null, // attachments
    null // to (null for list emails)
  );
};

/**
 * Send an instant-meeting invite to an ad-hoc recipient set (no saved list).
 * Used when the organiser builds the invitee list on the spot in the meeting creator.
 */
export const sendMeetingInviteAdHoc = async (
  recipients: AdHocInviteRecipient[],
  meetingName: string,
  url: string,
  attachments: InviteAttachments = []
) => {
  return await SendAdHocCANmail(
    recipients,
    MEET_FROM, // from
    MEET_FROM, // reply_to
    INVITE_SUBJECT, // subject
    buildInviteHtml(meetingName, url), // email_contents
    attachments && attachments.length ? attachments : null // attachments
  );
};
